// React Query hooks for surveys (/api/surveys). Kept apart from useQueries.js, which is already
// very long; the request path is the same shared apiRequest.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useAuthenticatedFetch from './useAuthenticatedFetch';
import { apiBinaryRequest, apiRequest } from './useQueries';

const useApi = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  return (endpoint, options) => apiRequest(authenticatedFetch, `/surveys${endpoint}`, options);
};

const KEYS = {
  all: ['surveys'],
  list: ['surveys', 'manage'],
  one: (id) => ['surveys', 'manage', id],
  people: ['surveys', 'people'],
};

// Every survey (for people who can create them).
export const useManagedSurveys = (options = {}) => {
  const api = useApi();
  return useQuery({ queryKey: KEYS.list, queryFn: () => api('/manage'), staleTime: 30 * 1000, ...options });
};

export const useManagedSurvey = (id) => {
  const api = useApi();
  return useQuery({ queryKey: KEYS.one(id), queryFn: () => api(`/manage/${id}`), enabled: Boolean(id) });
};

// Who a survey can be sent to.
export const useSurveyPeople = () => {
  const api = useApi();
  return useQuery({ queryKey: KEYS.people, queryFn: () => api('/manage/people'), staleTime: 5 * 60 * 1000 });
};

export const useSurveyRecipients = (id) => {
  const api = useApi();
  return useQuery({
    queryKey: ['surveys', 'recipients', id],
    queryFn: () => api(`/manage/${id}/recipients`),
    enabled: Boolean(id),
    refetchInterval: 5000,
  });
};

// A write that returns the survey: the list and that survey both refresh.
const useSurveyMutation = (request) => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars) => request(api, vars),
    onSuccess: async (survey) => {
      if (survey?.id) queryClient.setQueryData(KEYS.one(survey.id), survey);
      await queryClient.invalidateQueries({ queryKey: KEYS.list });
      if (survey?.id) await queryClient.invalidateQueries({ queryKey: ['surveys', 'recipients', survey.id] });
    },
  });
};

export const useCreateSurvey = () => useSurveyMutation((api, body) => api('/manage', { method: 'POST', body: body || {} }));
export const useSaveSurvey = () => useSurveyMutation((api, { id, ...body }) => api(`/manage/${id}`, { method: 'PUT', body }));
export const usePublishSurvey = () => useSurveyMutation((api, { id, ...body }) => api(`/manage/${id}/publish`, { method: 'POST', body }));
export const useCloseSurvey = () => useSurveyMutation((api, id) => api(`/manage/${id}/close`, { method: 'POST' }));
export const useReopenSurvey = () => useSurveyMutation((api, id) => api(`/manage/${id}/reopen`, { method: 'POST' }));
export const useDuplicateSurvey = () => useSurveyMutation((api, { id, title }) => api(`/manage/${id}/duplicate`, { method: 'POST', body: { title } }));

export const useDeleteSurvey = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api(`/manage/${id}`, { method: 'DELETE' }),
    onSuccess: async (_data, id) => {
      queryClient.removeQueries({ queryKey: KEYS.one(id) });
      await queryClient.invalidateQueries({ queryKey: KEYS.list });
    },
  });
};

// Everything sent back for a survey: { survey, people: [{ name, status, answers, ... }] }.
export const useSurveyResponses = (id) => {
  const api = useApi();
  return useQuery({ queryKey: ['surveys', 'responses', id], queryFn: () => api(`/manage/${id}/responses`), enabled: Boolean(id) });
};

// ---- Taking surveys ----

// The surveys sent to me (open ones, and closed ones I answered).
export const useMySurveys = (options = {}) => {
  const api = useApi();
  return useQuery({ queryKey: ['surveys', 'mine'], queryFn: () => api('/mine'), staleTime: 60 * 1000, ...options });
};

export const useMySurvey = (id) => {
  const api = useApi();
  return useQuery({ queryKey: ['surveys', 'mine', id], queryFn: () => api(`/mine/${id}`), enabled: Boolean(id) });
};

// Saves my answers; `submit: true` sends them.
export const useSaveMyResponse = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, answers, submit }) => api(`/mine/${id}/response`, { method: 'PUT', body: { answers, submit } }),
    onSuccess: async (survey) => {
      queryClient.setQueryData(['surveys', 'mine', survey.id], survey);
      await queryClient.invalidateQueries({ queryKey: ['surveys', 'mine'], exact: true });
      await queryClient.invalidateQueries({ queryKey: KEYS.list });
    },
  });
};

const readAsBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || '').replace(/^data:[^,]*,/, ''));
  reader.onerror = () => reject(new Error('That file could not be read.'));
  reader.readAsDataURL(file);
});

// Uploads an image into a survey: resolves to { id, fileName, contentType, sizeBytes }.
export const useUploadSurveyImage = () => {
  const api = useApi();
  return useMutation({
    mutationFn: async ({ surveyId, file }) => api(`/manage/${surveyId}/assets`, {
      method: 'POST',
      body: { file_name: file.name, content_base64: await readAsBase64(file) },
    }),
  });
};

/**
 * An uploaded survey image as an object URL. Images need the sign-in token, which an <img> can't
 * send, so they're fetched and handed over as a blob URL.
 */
export const useSurveyImageUrl = (surveyId, assetId) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const query = useQuery({
    queryKey: ['surveys', 'image', surveyId, assetId],
    queryFn: () => apiBinaryRequest(authenticatedFetch, `/surveys/${surveyId}/assets/${assetId}`),
    enabled: Boolean(surveyId && assetId),
    staleTime: 60 * 60 * 1000,
  });
  const [url, setUrl] = useState(null);
  const blob = query.data?.blob;
  useEffect(() => {
    if (!blob) {
      setUrl(null);
      return undefined;
    }
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [blob]);
  return { url, isLoading: query.isLoading, error: query.error };
};
